import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Truck, Plus, Trash, Check, CircleNotch, ArrowLeft
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function DDTBuilderPage() {
  const { id: ddtId } = useParams();
  const isEditMode = !!ddtId;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDifferentDest, setShowDifferentDest] = useState(false);
  const [isFirstDDT, setIsFirstDDT] = useState(false);
  const [startingNumber, setStartingNumber] = useState(1);

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    client_name: '',
    client_address: '',
    client_vat: '',
    client_fiscal_code: '',
    destination_address: '',
    causal: 'Vendita',
    transport_by: 'Mittente',
    carrier: '',
    parcels: 1,
    weight: '',
    appearance: '',
    transport_date: new Date().toISOString().split('T')[0],
    transport_time: '',
    notes: ''
  });

  const [items, setItems] = useState([
    { description: '', unit: 'pz', quantity: 1 }
  ]);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(Array.isArray(data) ? data : [])
    } catch (err) { console.error(err); }
  }, []);

  const checkFirstDDT = useCallback(async () => {
    if (isEditMode) return;
    try {
      const { data } = await axios.get(`${API}/ddt`);
      if ((data || []).length === 0) setIsFirstDDT(true);
    } catch (err) { console.error(err); }
  }, [isEditMode]);

  const fetchDDT = useCallback(async () => {
    if (!ddtId) return;
    try {
      const { data } = await axios.get(`${API}/ddt/${ddtId}`);
      setForm({
        date: data.date || '',
        client_name: data.client_name || '',
        client_address: data.client_address || '',
        client_vat: data.client_vat || '',
        client_fiscal_code: data.client_fiscal_code || '',
        destination_address: data.destination_address || '',
        causal: data.causal || 'Vendita',
        transport_by: data.transport_by || 'Mittente',
        carrier: data.carrier || '',
        parcels: data.parcels || 1,
        weight: data.weight || '',
        appearance: data.appearance || '',
        transport_date: data.transport_date || '',
        transport_time: data.transport_time || '',
        notes: data.notes || ''
      });
      setItems(data.items?.length ? data.items : [{ description: '', unit: 'pz', quantity: 1 }]);
      setSelectedClientId(data.client_id || '');
      if (data.destination_address && data.destination_address !== data.client_address) {
        setShowDifferentDest(true);
      }
    } catch (err) {
      toast.error(t('ddt.loadError'));
      navigate('/ddt');
    }
  }, [ddtId, navigate, t]);

  useEffect(() => { fetchClients(); fetchDDT(); checkFirstDDT(); }, [fetchClients, fetchDDT, checkFirstDDT]);

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    const client = (clients || []).find(c => c.id === clientId);
    if (client) {
      setForm(f => ({
        ...f,
        client_name: client.name || '',
        client_address: client.address || '',
        client_vat: client.vat_number || '',
        client_fiscal_code: client.fiscal_code || ''
      }));
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', unit: 'pz', quantity: 1 }]);
  };

  const removeItem = (index) => {
    if ((items || []).length <= 1) return;
    setItems((items || []).filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const handleSave = async () => {
    if (!form.client_name) {
      toast.error(t('ddt.selectClient'));
      return;
    }
    if ((items || []).length === 0 || !items[0].description) {
      toast.error(t('ddt.addItem'));
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      client_id: selectedClientId,
      destination_address: showDifferentDest ? form.destination_address : form.client_address,
      items: (items || []).filter(i => i.description),
      ...(isFirstDDT && startingNumber > 1 ? { starting_number: startingNumber } : {})
    };

    try {
      if (isEditMode) {
        await axios.put(`${API}/ddt/${ddtId}`, payload);
        toast.success(t('ddt.updated'));
        navigate('/ddt');
      } else {
        await axios.post(`${API}/ddt`, payload);
        toast.success(t('ddt.created'));
        navigate('/ddt');
      }
    } catch (err) {
      toast.error(isEditMode ? t('ddt.updateError') : t('ddt.createError'));
    }
    setSaving(false);
  };

  const causalOptions = ['Vendita', 'Conto Lavoro', 'Conto Visione', 'Reso', 'Omaggio', 'Riparazione', 'Trasferimento'];
  const transportOptions = ['Mittente', 'Destinatario', 'Vettore'];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ddt')} data-testid="back-to-ddt">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0A0F1A] flex items-center gap-2">
            <Truck size={24} weight="duotone" className="text-[#0033A0]" />
            {isEditMode ? t('ddt.editDdt') : t('ddt.newDdt')}
          </h1>
        </div>
      </div>

      {/* Starting number for first DDT */}
      {isFirstDDT && !isEditMode && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">{t('ddt.firstDocNote')}</p>
              <p className="text-xs text-amber-600 mt-1">{t('ddt.firstDocHint')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-amber-800 whitespace-nowrap">N. partenza:</Label>
              <Input
                type="number" min="1"
                value={startingNumber}
                onChange={e => setStartingNumber(parseInt(e.target.value) || 1)}
                className="w-20 bg-white"
                data-testid="ddt-starting-number"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 1: Client */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('ddt.section1')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t('ddt.selectClient')}</Label>
              <Select value={selectedClientId} onValueChange={handleClientSelect}>
                <SelectTrigger className="mt-1" data-testid="ddt-client-select">
                  <SelectValue placeholder={t('ddt.selectClientPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(clients || []).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('ddt.date')}</Label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                className="mt-1"
                data-testid="ddt-date"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t('ddt.client')}</Label>
              <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} className="mt-1" data-testid="ddt-client-name" />
            </div>
            <div>
              <Label className="text-xs">{t('common.address')}</Label>
              <Input value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })} className="mt-1" data-testid="ddt-client-address" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">P.IVA</Label>
              <Input value={form.client_vat} onChange={e => setForm({ ...form, client_vat: e.target.value })} className="mt-1" data-testid="ddt-client-vat" />
            </div>
            <div>
              <Label className="text-xs">C.F.</Label>
              <Input value={form.client_fiscal_code} onChange={e => setForm({ ...form, client_fiscal_code: e.target.value })} className="mt-1" data-testid="ddt-client-cf" />
            </div>
          </div>

          {/* Different destination */}
          <label className="flex items-center gap-2 cursor-pointer" data-testid="ddt-different-dest-toggle">
            <input
              type="checkbox"
              checked={showDifferentDest}
              onChange={e => setShowDifferentDest(e.target.checked)}
              className="rounded border-[#CBD5E0] text-[#0033A0] focus:ring-[#0033A0]"
            />
            <span className="text-xs text-[#4A5568]">{t('ddt.destinationDifferent')}</span>
          </label>

          {showDifferentDest && (
            <div>
              <Label className="text-xs">{t('ddt.destinationAddress')}</Label>
              <Input
                value={form.destination_address}
                onChange={e => setForm({ ...form, destination_address: e.target.value })}
                className="mt-1"
                data-testid="ddt-dest-address"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Transport */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('ddt.section2')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t('ddt.causal')}</Label>
              <Select value={form.causal} onValueChange={v => setForm({ ...form, causal: v })}>
                <SelectTrigger className="mt-1" data-testid="ddt-causal">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {causalOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('ddt.transportBy')}</Label>
              <Select value={form.transport_by} onValueChange={v => setForm({ ...form, transport_by: v })}>
                <SelectTrigger className="mt-1" data-testid="ddt-transport-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {transportOptions.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">{t('ddt.carrier')}</Label>
              <Input value={form.carrier} onChange={e => setForm({ ...form, carrier: e.target.value })} className="mt-1" data-testid="ddt-carrier" />
            </div>
            <div>
              <Label className="text-xs">{t('ddt.appearance')}</Label>
              <Input value={form.appearance} onChange={e => setForm({ ...form, appearance: e.target.value })} className="mt-1" placeholder="es. Scatole, Bancali..." data-testid="ddt-appearance" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">{t('ddt.parcels')}</Label>
              <Input type="number" min="1" value={form.parcels} onChange={e => setForm({ ...form, parcels: parseInt(e.target.value) || 1 })} className="mt-1" data-testid="ddt-parcels" />
            </div>
            <div>
              <Label className="text-xs">{t('ddt.weight')}</Label>
              <Input value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} className="mt-1" placeholder="es. 150 kg" data-testid="ddt-weight" />
            </div>
            <div>
              <Label className="text-xs">{t('ddt.transportDate')}</Label>
              <Input type="date" value={form.transport_date} onChange={e => setForm({ ...form, transport_date: e.target.value })} className="mt-1" data-testid="ddt-transport-date" />
            </div>
            <div>
              <Label className="text-xs">{t('ddt.transportTime')}</Label>
              <Input type="time" value={form.transport_time} onChange={e => setForm({ ...form, transport_time: e.target.value })} className="mt-1" data-testid="ddt-transport-time" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Items */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('ddt.section3')}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="ddt-add-item">
            <Plus size={16} className="mr-1" />
            {t('ddt.addItem')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(items || []).map((item, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-[#F4F6F8] rounded-lg" data-testid={`ddt-item-${index}`}>
              <span className="text-xs font-bold text-[#0033A0] mt-2 w-6 text-center">{index + 1}</span>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-7">
                  <Label className="text-[10px] text-[#4A5568]">{t('ddt.description')}</Label>
                  <Input
                    value={item.description}
                    onChange={e => updateItem(index, 'description', e.target.value)}
                    className="mt-0.5 bg-white text-sm"
                    placeholder="Descrizione articolo..."
                    data-testid={`ddt-item-desc-${index}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] text-[#4A5568]">{t('ddt.unit')}</Label>
                  <Select value={item.unit} onValueChange={v => updateItem(index, 'unit', v)}>
                    <SelectTrigger className="mt-0.5 bg-white text-sm" data-testid={`ddt-item-unit-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pz">pz</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="mq">mq</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="lt">lt</SelectItem>
                      <SelectItem value="nr">nr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] text-[#4A5568]">{t('ddt.quantity')}</Label>
                  <Input
                    type="number" min="1"
                    value={item.quantity}
                    onChange={e => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                    className="mt-0.5 bg-white text-sm"
                    data-testid={`ddt-item-qty-${index}`}
                  />
                </div>
                <div className="sm:col-span-1 flex items-end">
                  {(items || []).length > 1 && (
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:bg-red-50 h-9 w-9"
                      data-testid={`ddt-item-remove-${index}`}
                    >
                      <Trash size={16} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Section 4: Notes */}
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold text-[#0033A0] uppercase tracking-wider">
            {t('ddt.section4')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            rows={3}
            placeholder={t('ddt.notesPlaceholder')}
            className="border-[#E2E8F0]"
            data-testid="ddt-notes"
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/ddt')} data-testid="ddt-cancel">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0033A0] hover:bg-[#0047AB] text-white min-w-[180px]"
          data-testid="ddt-save"
        >
          {saving ? (
            <><CircleNotch size={18} className="mr-2 animate-spin" /> {isEditMode ? t('ddt.saving') : t('ddt.creating')}</>
          ) : (
            <><Check size={18} className="mr-2" /> {isEditMode ? t('ddt.saveDdt') : t('ddt.createDdt')}</>
          )}
        </Button>
      </div>
    </div>
  );
}

